import {createStyles} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import OptionContent from 'ui-library/menus/OptionContent';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import SuggestedPlotsPanel from './SuggestedPlotsPanel';

const PANEL_WIDTH = 350;

interface SuggestedPlotsHoverOptionProps {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
  onSuggestionChosen?: () => void;
}

export default function SuggestedPlotsHoverOption({
  entryId,
  tableName,
  getTableData,
  onSuggestionChosen,
}: SuggestedPlotsHoverOptionProps): JSX.Element {
  const {classes} = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropLeft, setDropLeft] = useState(false);

  // The suggestions fetch is kicked off when the dropdown opens (see SuggestedPlotsDropdown),
  // so hovering only needs to track engagement and decide which side to open the panel on.
  const handleMouseEnter = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.HOVERED_AI_SUGGESTED_PLOTS_OPTION, {
      actionOrigin: ActionOrigin.NOTEBOOK,
    });
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropLeft(window.innerWidth - rect.right < PANEL_WIDTH);
    }
  }, []);

  return (
    <Float
      interaction={FloatingInteraction.HOVER}
      placement={dropLeft ? FloatingPlacement.LEFT_START : FloatingPlacement.RIGHT_START}
      delay={{open: 200, close: 400}}
      restMs={40}
      stealFocus={false}
      role="dialog"
      floatingElementOptions={{
        floatingBodyClassName: classes.floatingBody,
      }}
      renderContent={() => (
        <SuggestedPlotsPanel
          entryId={entryId}
          tableName={tableName}
          getTableData={getTableData}
          onClose={onSuggestionChosen}
          autoFetch={true}
        />
      )}
    >
      <div ref={containerRef} onMouseEnter={handleMouseEnter} className={classes.optionWrapper}>
        <OptionContent hasSubmenu={true}>AI Suggested Plots</OptionContent>
      </div>
    </Float>
  );
}

const useStyles = createStyles((theme) => ({
  optionWrapper: {
    width: '100%',
  },
  floatingBody: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.md,
  },
}));
